import { useEffect, useState, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import appConfig from 'config/appConfig'
import routesConfig from 'lib/routesConfig'
import { useApi } from 'v2/modules/lib/hooks/useApi'
import { DeliverActionProps } from 'components/pages/quarantineAction/DeliverAction'
import { AllowActionProps } from 'components/pages/quarantineAction/AllowAction'
import { BlockActionProps } from 'components/pages/quarantineAction/BlockAction'
import { ErrorProps } from 'components/pages/quarantineAction/Error'

type JwtReturnedValue = {
  user: string
  headerFrom: string
  subject: string
  action: 'blocklist' | 'deliver' | 'whitelist'
  date: string
}

export interface UseQn2cFeatureLogicProps {
  jwtReturnedValue: JwtReturnedValue | null
  error: string
  loading: boolean
  mapDeliverActionProps: () => DeliverActionProps
  mapAllowActionProps: () => AllowActionProps
  mapBlockActionProps: () => BlockActionProps
  mapErrorActionProps: () => ErrorProps
}

const useQn2cFeatureLogic = (): UseQn2cFeatureLogicProps => {
  const [jwtReturnedValue, setJwtReturnedValue] = useState<JwtReturnedValue | null>(null)
  const [error, setError] = useState<string>('')
  const [delivered, setDelivered] = useState<boolean>(false)
  const [allowed, setAllowed] = useState<boolean>(false)
  const [blocked, setBlocked] = useState<boolean>(false)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [searchParams] = useSearchParams()
  const token = searchParams.get('qn_jwt') || ''
  const adminBlockErrorMessage =
    'This action has been disabled by your IT admin. Contact your IT admin for more information.'

  const api = useApi({
    host: appConfig.GATEWAY_URL ? `${appConfig.GATEWAY_URL}/v2` : '',
    accessToken: ''
  })

  // verifyAction logic
  useEffect(() => {
    const verifyToken = async () => {
      setLoading(true)
      try {
        const result = await api.qn2cService.verifyQn2cToken(token)
        setJwtReturnedValue({
          user: result.claims.user,
          // API returns headrFrm instead of headerFrom
          headerFrom: result.claims.headerFrm,
          subject: result.claims.subject,
          action: result.claims.action,
          date: result.claims.date
        })
      } catch (err: any) {
        setError(err?.response?.data.detail.message || 'Failed to verify qn2c token with Unknown error')
      } finally {
        setLoading(false)
      }
    }

    if (token) {
      verifyToken()
    } else {
      setError('Something went wrong. Exit this page and try again.')
    }
  }, [api.qn2cService, token])

  // DeliverAction logic
  const executeDeliver = useCallback(async () => {
    setActionLoading(true)
    try {
      await api.qn2cService.qn2cDeliverAction(token)
      setDelivered(true)
      setError('')
    } catch (err: any) {
      setError(
        typeof err?.response?.data?.detail?.message === 'string' &&
          err.response.data.detail.message.includes('Forbidden by admin policy')
          ? adminBlockErrorMessage
          : 'Failed to deliver message. Please try again.'
      )
    } finally {
      setActionLoading(false)
    }
  }, [api.qn2cService, token])

  const goToLogin = useCallback(() => {
    routesConfig.LOGIN.goto()
  }, [])

  const mapDeliverActionProps = useCallback(
    (): DeliverActionProps => ({
      user: jwtReturnedValue?.user ?? '',
      headerFrom: jwtReturnedValue?.headerFrom ?? '',
      subject: jwtReturnedValue?.subject ?? '',
      date: jwtReturnedValue?.date ?? '',
      onClick: delivered ? goToLogin : executeDeliver,
      buttonLabel: delivered ? 'Open Email Gateway Defense' : 'Deliver',
      buttonBdsType: delivered ? 'interactiveNeutral' : 'interactiveEmphasis',
      content: delivered
        ? {
            header: 'Message delivered',
            subHeader: 'You may now close this window.'
          }
        : {
            header: 'Deliver message?',
            subHeader: 'The following message will be released to your primary inbox.'
          },
      error: !!error,
      errorContent: error,
      loading: actionLoading,
      icon: delivered
    }),
    [jwtReturnedValue, error, actionLoading, delivered, goToLogin, executeDeliver]
  )

  // AllowAction logic
  const executeAllow = useCallback(async () => {
    setActionLoading(true)
    try {
      await api.qn2cService.qn2cAllowAction(token)
      setAllowed(true)
      setError('')
    } catch (err: any) {
      setError(
        typeof err?.response?.data?.detail?.message === 'string' &&
          err.response.data.detail.message.includes('Forbidden by admin policy')
          ? adminBlockErrorMessage
          : 'Failed to deliver message to inbox and add sender to allow list. Please try again.'
      )
    } finally {
      setActionLoading(false)
    }
  }, [api.qn2cService, token])

  const initial = useMemo(() => {
    const namePart = jwtReturnedValue?.headerFrom.split('<')[0].trim()
    const result = namePart?.match(/(\b\S)?/g)?.join('') || ''
    return result.toUpperCase().slice(0, 2)
  }, [jwtReturnedValue?.headerFrom])

  const mapAllowActionProps = useCallback(
    (): AllowActionProps => ({
      user: jwtReturnedValue?.user ?? '',
      headerFrom: jwtReturnedValue?.headerFrom ?? '',
      subject: jwtReturnedValue?.subject ?? '',
      date: jwtReturnedValue?.date ?? '',
      onClick: allowed ? goToLogin : executeAllow,
      buttonLabel: allowed ? 'Open Email Gateway Defense' : 'Deliver message and allow sender',
      buttonBdsType: allowed ? 'interactiveNeutral' : 'interactiveEmphasis',
      error: !!error,
      errorContent: error,
      loading: actionLoading,
      icon: allowed,
      initial,
      content: allowed ? 'allowSuccess' : 'allowContent'
    }),
    [jwtReturnedValue, allowed, goToLogin, executeAllow, error, actionLoading, initial]
  )

  // BlockAction logic
  const executeBlock = useCallback(async () => {
    setActionLoading(true)
    try {
      await api.qn2cService.qn2cBlockAction(token)
      setBlocked(true)
      setError('')
    } catch (err: any) {
      setError(
        typeof err?.response?.data?.detail?.message === 'string' &&
          err.response.data.detail.message.includes('Forbidden by admin policy')
          ? adminBlockErrorMessage
          : 'Failed to add sender to block list. Please try again.'
      )
    } finally {
      setActionLoading(false)
    }
  }, [api.qn2cService, token])

  const mapBlockActionProps = useCallback(
    (): BlockActionProps => ({
      user: jwtReturnedValue?.user ?? '',
      headerFrom: jwtReturnedValue?.headerFrom ?? '',
      onClick: blocked ? goToLogin : executeBlock,
      buttonLabel: blocked ? 'Open Email Gateway Defense' : 'Block sender',
      buttonBdsType: blocked ? 'interactiveNeutral' : 'interactiveEmphasis',
      error: !!error,
      errorContent: error,
      loading: actionLoading,
      icon: blocked,
      initial,
      content: blocked
        ? {
            header: 'Sender blocked',
            subHeader: 'We will block all future emails from this sender. You may now close this window.'
          }
        : { header: 'Always block sender?', subHeader: 'We will add this sender to your block list' }
    }),
    [jwtReturnedValue, blocked, goToLogin, executeBlock, error, actionLoading, initial]
  )

  // Error page
  const expiredToken = error === 'Qn2c Verify Action Error: JWT Verification Failed. previous token has expired'
  const errorText = expiredToken
    ? 'Link has expired. You may view the message in Email Gateway Defense.'
    : 'Something went wrong. Exit this page and try again.'

  const mapErrorActionProps = useCallback(
    (): ErrorProps => ({
      errorMessage: errorText,
      expired: expiredToken,
      onClick: goToLogin
    }),
    [errorText, expiredToken, goToLogin]
  )

  return {
    jwtReturnedValue,
    error,
    loading,
    mapDeliverActionProps,
    mapAllowActionProps,
    mapBlockActionProps,
    mapErrorActionProps
  }
}

export default useQn2cFeatureLogic
